/**
 * AppICEMAP — PersonalDashboard
 * Custom element: `<personal-dashboard>`
 *
 * Displays personal stats: karma, report counts, strikes, activity sparkline.
 *
 * Props:
 *   stats { object } — from fetchPersonalStats()
 *   activity { Array } — from fetchActivityTimeline()
 *   user { object }   — from store state.user
 */

import { state } from "../../store/index.js";
import iconCss from "../../../css/utilities/phosphor-shadow.css?inline";

/**
 * Karma → rango con nombre y una frase que explique qué significa.
 * Los umbrales son los mismos que usa el perfil, para que la app cuente una
 * sola historia sobre el karma.
 */
const RANKS = [
	{ min: 0, name: "Novato", blurb: "Acabas de llegar. Cada reporte confirmado suma karma." },
	{ min: 10, name: "Vecino", blurb: "Ya eres parte del barrio: la comunidad empieza a confiar en ti." },
	{ min: 50, name: "Colaborador", blurb: "Reportas con constancia y se nota en el mapa." },
	{ min: 100, name: "Confiable", blurb: "Tus avisos pesan: se muestran antes que los de cuentas nuevas." },
	{ min: 250, name: "Guardián", blurb: "Referente de la zona. Muy poca gente llega hasta aquí." },
	{ min: 500, name: "Leyenda", blurb: "Máximo rango. La comunidad te debe unas cuantas." },
];

/**
 * @param {number} karma
 * @returns {{name: string, blurb: string, next: number|null, nextName: string}}
 */
function getRank(karma) {
	let idx = 0;
	for (let i = RANKS.length - 1; i >= 0; i--) {
		if (karma >= RANKS[i].min) {
			idx = i;
			break;
		}
	}
	const next = RANKS[idx + 1] || null;
	return {
		name: RANKS[idx].name,
		blurb: RANKS[idx].blurb,
		next: next ? next.min : null,
		nextName: next ? next.name : "",
	};
}

export class PersonalDashboard extends HTMLElement {
	constructor() {
		super();
		this.attachShadow({ mode: "open" });
	}

	static get observedAttributes() {
		return ["loading"];
	}

	connectedCallback() {
		this._render();
	}

	attributeChangedCallback(name, oldVal, newVal) {
		if (name === "loading") {
			this._render();
		}
	}

	/** @param {object | null} val */
	set stats(val) {
		this._stats = val;
		this._render();
	}
	get stats() {
		return this._stats;
	}

	/** @param {Array | null} val */
	set activity(val) {
		this._activity = val;
		this._renderSparkline();
	}
	get activity() {
		return this._activity;
	}

	/* ── Render ──────────────────────────────────────────────────────────── */

	_render() {
		const isLoading = this.hasAttribute("loading");
		const stats = this._stats;
		const user = state.user;

		const karma = user?.karma ?? stats?.karma ?? 0;
		const strikes = user?.strikes ?? stats?.strikes ?? 0;
		const totalReports = stats?.totalReports ?? 0;
		const confirmed = stats?.confirmed ?? 0;
		const denied = stats?.denied ?? 0;
		const pending = stats?.pending ?? 0;
		const confirmedPct =
			totalReports > 0 ? Math.round((confirmed / totalReports) * 100) : 0;
		const deniedPct =
			totalReports > 0 ? Math.round((denied / totalReports) * 100) : 0;

		this.shadowRoot.innerHTML = `
			<style>${iconCss}
				:host { display: block; }
				.pd { display: flex; flex-direction: column; gap: var(--space-4, 16px); }
				.pd--loading { opacity: 0.5; pointer-events: none; }

				/* Karma hero */
				.pd-karma {
					display: flex; flex-direction: column; align-items: center;
					padding: var(--space-6, 24px) var(--space-4, 16px);
					background: var(--color-surface, #232532);
					border-radius: var(--radius-lg, 14px);
					box-shadow: var(--shadow-sm);
				}
				.pd-karma__ring {
					position: relative;
					width: 96px; height: 96px;
					border-radius: 50%;
					display: flex; align-items: center; justify-content: center;
					margin-bottom: var(--space-3, 12px);
				}
				.pd-karma__ring::before {
					content: '';
					position: absolute; inset: 0;
					border-radius: 50%;
					background: conic-gradient(
						var(--color-accent, #9184d9) 0deg,
						var(--color-accent, #9184d9) calc(var(--karma-deg, 0deg)),
						var(--color-neutral-800, #3f424d) calc(var(--karma-deg, 0deg))
					);
					mask: radial-gradient(transparent 62%, #000 63%);
					-webkit-mask: radial-gradient(transparent 62%, #000 63%);
				}
				.pd-karma__num {
					font-size: var(--text-2xl, 2rem);
					font-weight: 700;
					color: var(--color-text, #e9e9ed);
					line-height: var(--leading-2xl, 1.05);
				}
				.pd-karma__label {
					font-size: var(--text-sm, 0.8125rem);
					color: var(--color-neutral-400, #b2b6ca);
				}

				/* Report stats grid */
				.pd-grid {
					display: grid;
					grid-template-columns: repeat(2, 1fr);
					gap: var(--space-3, 12px);
				}
				.pd-stat {
					display: flex; flex-direction: column; align-items: center;
					padding: var(--space-4, 16px);
					background: var(--color-surface, #232532);
					border-radius: var(--radius-md, 8px);
					box-shadow: var(--shadow-sm);
				}
				.pd-stat__val {
					font-size: var(--text-xl, 1.5rem);
					font-weight: 700;
					color: var(--color-text, #e9e9ed);
					line-height: var(--leading-xl, 1.1);
				}
				.pd-stat__label {
					font-size: var(--text-xs, 0.6875rem);
					color: var(--color-neutral-400, #b2b6ca);
					margin-top: var(--space-1, 4px);
				}
				.pd-stat--accent .pd-stat__val { color: var(--color-accent, #9184d9); }
				.pd-stat--success .pd-stat__val { color: var(--color-success, #22c55e); }
				.pd-stat--danger .pd-stat__val { color: var(--color-danger, #ef4444); }
				.pd-stat--warning .pd-stat__val { color: var(--color-warning, #fbbf24); }

				/* Progress bars */
				.pd-bars { display: flex; flex-direction: column; gap: var(--space-2, 8px); }
				.pd-bar-row {
					display: flex; align-items: center; gap: var(--space-2, 8px);
				}
				.pd-bar-row__label {
					font-size: var(--text-xs, 0.6875rem);
					color: var(--color-neutral-400, #b2b6ca);
					width: 60px; flex-shrink: 0;
				}
				.pd-bar-row__track {
					flex: 1; height: 8px;
					background: var(--color-neutral-800, #3f424d);
					border-radius: var(--radius-full, 9999px);
					overflow: hidden;
				}
				.pd-bar-row__fill {
					height: 100%; border-radius: var(--radius-full, 9999px);
					transition: width var(--duration-normal, 250ms) var(--easing-standard);
				}
				.pd-bar-row__pct {
					font-size: var(--text-xs, 0.6875rem);
					color: var(--color-neutral-400, #b2b6ca);
					width: 32px; text-align: right; flex-shrink: 0;
				}

				/* Strikes */
				.pd-strikes {
					display: flex; align-items: center; gap: var(--space-3, 12px);
					padding: var(--space-3, 12px) var(--space-4, 16px);
					background: var(--color-surface, #232532);
					border-radius: var(--radius-md, 8px);
					box-shadow: var(--shadow-sm);
				}
				.pd-strikes__icon {
					font-size: 1.25rem;
					width: 36px; height: 36px;
					display: flex; align-items: center; justify-content: center;
					border-radius: 50%;
					background: color-mix(in srgb, var(--color-warning, #fbbf24) 15%, transparent);
					color: var(--color-warning, #fbbf24);
				}
				.pd-strikes__info { flex: 1; }
				.pd-strikes__count {
					font-size: var(--text-base, 0.9375rem);
					font-weight: 600;
					color: var(--color-text, #e9e9ed);
				}
				.pd-strikes__hint {
					font-size: var(--text-xs, 0.6875rem);
					color: var(--color-neutral-400, #b2b6ca);
				}
				.pd-strikes__badge {
					font-size: var(--text-xs, 0.6875rem);
					padding: 2px 10px;
					border-radius: var(--radius-full, 9999px);
					background: color-mix(in srgb, var(--color-warning, #fbbf24) 15%, transparent);
					color: var(--color-warning, #fbbf24);
					font-weight: 600;
				}

				/* Sparkline */
				.pd-sparkline {
					padding: var(--space-4, 16px);
					background: var(--color-surface, #232532);
					border-radius: var(--radius-lg, 14px);
					box-shadow: var(--shadow-sm);
				}
				.pd-sparkline__title {
					font-size: var(--text-xs, 0.6875rem);
					font-weight: 600;
					color: var(--color-neutral-400, #b2b6ca);
					text-transform: uppercase;
					letter-spacing: var(--tracking-uppercase, 0.08em);
					margin-bottom: var(--space-3, 12px);
				}
				.pd-sparkline__bars {
					display: flex; align-items: flex-end; gap: 2px;
					height: 48px;
				}
				.pd-sparkline__bar {
					flex: 1; min-width: 2px;
					background: var(--color-accent, #9184d9);
					border-radius: 2px 2px 0 0;
					transition: height var(--duration-normal, 250ms) var(--easing-standard);
					opacity: 0.7;
				}
				.pd-sparkline__bar--active { opacity: 1; }
				.pd-sparkline__labels {
					display: flex; justify-content: space-between;
					margin-top: var(--space-1, 4px);
					font-size: 9px; color: var(--color-neutral-500, #9397ab);
				}

				/* Rango */
				.pd-rank {
					display: inline-block;
					margin-top: var(--space-2, 8px);
					padding: 4px 12px;
					border-radius: 999px;
					background: color-mix(in srgb, var(--color-accent) 18%, transparent);
					border: 1px solid color-mix(in srgb, var(--color-accent) 34%, transparent);
					color: var(--color-accent-700, #d2cefd);
					font-size: var(--text-xs, 11px);
					font-weight: 600;
					letter-spacing: 0.06em;
					text-transform: uppercase;
				}
				.pd-rank__blurb {
					margin: var(--space-2, 8px) auto 0;
					max-width: 30ch;
					font-size: var(--text-xs, 11px);
					line-height: 1.5;
					color: var(--color-neutral-400, #b2b6ca);
				}
				.pd-rank__next {
					margin: 4px 0 0;
					font-size: 10px;
					color: var(--color-neutral-600, #75798c);
				}

				/* Insignias: acierto y racha */
				.pd-badges {
					display: flex;
					gap: var(--space-2, 8px);
					margin-bottom: var(--space-4, 16px);
				}
				.pd-badge {
					flex: 1;
					display: flex;
					flex-direction: column;
					align-items: center;
					gap: 2px;
					padding: var(--space-3, 12px);
					border-radius: var(--radius-md, 10px);
					background: var(--color-surface, #232532);
					border: 1px solid var(--color-divider);
				}
				.pd-badge i {
					font-size: 18px;
					color: var(--color-neutral-500, #9397ab);
					margin-bottom: 2px;
				}
				.pd-badge--hot i { color: #f0842f; }
				.pd-badge__val {
					font-size: var(--text-md, 17px);
					font-weight: 600;
					color: var(--color-text, #e9e9ed);
				}
				.pd-badge__label {
					font-size: 10px;
					text-transform: uppercase;
					letter-spacing: 0.06em;
					color: var(--color-neutral-600, #75798c);
				}

				/* Loading */
				.pd-skeleton {
					background: var(--color-neutral-800, #3f424d);
					border-radius: var(--radius-md, 8px);
					animation: pd-pulse 1.5s ease-in-out infinite;
				}
				@keyframes pd-pulse {
					0%, 100% { opacity: 0.4; }
					50% { opacity: 0.7; }
				}
			</style>

			<div class="pd ${isLoading ? "pd--loading" : ""}">
				${isLoading ? this._renderSkeleton() : this._renderContent(karma, strikes, totalReports, confirmed, denied, pending, confirmedPct, deniedPct, stats)}
			</div>
		`;
	}

	_renderSkeleton() {
		return `
			<div class="pd-skeleton" style="height:160px"></div>
			<div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
				<div class="pd-skeleton" style="height:72px"></div>
				<div class="pd-skeleton" style="height:72px"></div>
				<div class="pd-skeleton" style="height:72px"></div>
				<div class="pd-skeleton" style="height:72px"></div>
			</div>
			<div class="pd-skeleton" style="height:80px"></div>
		`;
	}

	_renderContent(
		karma,
		strikes,
		totalReports,
		confirmed,
		denied,
		pending,
		confirmedPct,
		deniedPct,
		stats,
	) {
		const karmaDeg = Math.min(360, (karma / 500) * 360);
		const rank = getRank(karma);
		const accuracy = stats?.accuracy ?? null;
		const streak = stats?.streak ?? 0;

		return `
			<div class="pd-karma" style="--karma-deg:${karmaDeg}deg">
				<div class="pd-karma__ring">
					<span class="pd-karma__num">${karma}</span>
				</div>
				<span class="pd-karma__label">Puntos de karma</span>
				<span class="pd-rank">${rank.name}</span>
				<p class="pd-rank__blurb">${rank.blurb}</p>
				${
					rank.next
						? `<p class="pd-rank__next">${rank.next - karma} pts para ${rank.nextName}</p>`
						: ""
				}
			</div>

			${
				totalReports > 0
					? `<div class="pd-badges">
				${
					accuracy !== null
						? `<div class="pd-badge">
					<i class="ph ph-target" aria-hidden="true"></i>
					<span class="pd-badge__val">${accuracy}%</span>
					<span class="pd-badge__label">Acierto</span>
				</div>`
						: ""
				}
				<div class="pd-badge${streak > 0 ? " pd-badge--hot" : ""}">
					<i class="ph ph-fire" aria-hidden="true"></i>
					<span class="pd-badge__val">${streak}</span>
					<span class="pd-badge__label">${streak === 1 ? "día seguido" : "días seguidos"}</span>
				</div>
			</div>`
					: ""
			}

			<div class="pd-grid">
				<div class="pd-stat">
					<span class="pd-stat__val">${totalReports}</span>
					<span class="pd-stat__label">Total reportes</span>
				</div>
				<div class="pd-stat pd-stat--success">
					<span class="pd-stat__val">${confirmed}</span>
					<span class="pd-stat__label">Confirmados</span>
				</div>
				<div class="pd-stat pd-stat--danger">
					<span class="pd-stat__val">${denied}</span>
					<span class="pd-stat__label">Negados</span>
				</div>
				<div class="pd-stat pd-stat--accent">
					<span class="pd-stat__val">${pending}</span>
					<span class="pd-stat__label">Pendientes</span>
				</div>
			</div>

			${
				totalReports > 0
					? `
			<div class="pd-bars">
				<div class="pd-bar-row">
					<span class="pd-bar-row__label">Confirm.</span>
					<div class="pd-bar-row__track">
						<div class="pd-bar-row__fill" style="width:${confirmedPct}%;background:var(--color-success,#22c55e)"></div>
					</div>
					<span class="pd-bar-row__pct">${confirmedPct}%</span>
				</div>
				<div class="pd-bar-row">
					<span class="pd-bar-row__label">Negados</span>
					<div class="pd-bar-row__track">
						<div class="pd-bar-row__fill" style="width:${deniedPct}%;background:var(--color-danger,#ef4444)"></div>
					</div>
					<span class="pd-bar-row__pct">${deniedPct}%</span>
				</div>
			</div>
			`
					: ""
			}

			<div class="pd-strikes">
				<div class="pd-strikes__icon">
					<i class="ph ph-warning"></i>
				</div>
				<div class="pd-strikes__info">
					<div class="pd-strikes__count">${strikes} aviso${strikes !== 1 ? "s" : ""}</div>
					<div class="pd-strikes__hint">${strikes > 0 ? "Tus reportes tienen visibilidad reducida" : "Sin avisos — ¡sigue así!"}</div>
				</div>
				${strikes > 0 ? `<span class="pd-strikes__badge">! Limitado</span>` : ""}
			</div>

			<div class="pd-sparkline">
				<div class="pd-sparkline__title">Actividad — últimos 7 días</div>
				<div class="pd-sparkline__bars" id="pd-sparkline-bars"></div>
				<div class="pd-sparkline__labels" id="pd-sparkline-labels"></div>
			</div>
		`;
	}

	_renderSparkline() {
		const barsEl = this.shadowRoot?.getElementById("pd-sparkline-bars");
		const labelsEl = this.shadowRoot?.getElementById("pd-sparkline-labels");
		if (!barsEl || !labelsEl) return;

		const activity = this._activity || [];
		// Show last 7 days
		const last7 = activity.slice(-7);
		const maxCount = Math.max(1, ...last7.map((d) => d.count));

		barsEl.innerHTML = last7
			.map((d) => {
				const h = Math.max(4, (d.count / maxCount) * 44);
				const active = d.count > 0 ? " pd-sparkline__bar--active" : "";
				return `<div class="pd-sparkline__bar${active}" style="height:${h}px" title="${d.date}: ${d.count}"></div>`;
			})
			.join("");

		labelsEl.innerHTML = last7
			.map((d) => {
				const dayName = new Date(d.date + "T12:00")
					.toLocaleDateString("es", { weekday: "short" })
					.replace(".", "");
				return `<span>${dayName}</span>`;
			})
			.join("");
	}
}

if (!customElements.get("personal-dashboard")) {
	customElements.define("personal-dashboard", PersonalDashboard);
}
